import { useEffect, useState } from "react";
import { Plus, Trash2, Pencil } from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { toast } from "../ui/sonner";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Switch } from "../ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { ConfirmDialog } from "./ConfirmDialog";

const EMPTY = {
  name: "", description: "", active: true, start_date: "", end_date: "",
  discount_type: "percentage", discount_value: 10, applies_to: "strangers",
  scope: "all", scope_id: "", promo_code: "", usage_limit: "", per_user_limit: "",
};

export const PromotionsPanel = () => {
  const [promos, setPromos] = useState(null);
  const [form, setForm] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = () => api.get("/admin/promotions").then(({ data }) => setPromos(data));

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY);
  };

  const openEdit = (p) => {
    setEditingId(p.id);
    setForm({ ...EMPTY, ...p, usage_limit: p.usage_limit ?? "", per_user_limit: p.per_user_limit ?? "" });
  };

  const save = async () => {
    setSaving(true);
    const payload = {
      ...form,
      discount_value: Number(form.discount_value),
      usage_limit: form.usage_limit === "" ? null : Number(form.usage_limit),
      per_user_limit: form.per_user_limit === "" ? null : Number(form.per_user_limit),
      scope_id: form.scope_id || null,
      promo_code: form.promo_code || null,
    };
    try {
      if (editingId) {
        await api.put(`/admin/promotions/${editingId}`, payload);
        toast.success("Promotion updated");
      } else {
        await api.post("/admin/promotions", payload);
        toast.success("Promotion created");
      }
      setForm(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    await api.delete(`/admin/promotions/${id}`);
    toast.success("Promotion deleted");
    load();
  };

  return (
    <div data-testid="promotions-panel">
      <div className="flex justify-between items-center mb-6">
        <p className="text-xs font-medium text-midnight-black/60 max-w-md">Promotions apply automatically to eligible bookings when no session-specific fee override is set.</p>
        <Button data-testid="create-promotion-button" onClick={openCreate} className="bg-stranger-orange hover:bg-orange-600 text-clean-white font-bold rounded-full shrink-0">
          <Plus className="w-4 h-4 mr-1.5" /> NEW PROMOTION
        </Button>
      </div>

      {promos === null ? (
        <p className="font-medium text-midnight-black/60">Loading promotions...</p>
      ) : promos.length === 0 ? (
        <p className="font-medium text-midnight-black/60" data-testid="no-promotions">No promotions created yet.</p>
      ) : (
        <div className="space-y-3" data-testid="promotions-list">
          {promos.map((p) => (
            <div key={p.id} className="border border-soft-gray rounded-xl p-4 flex items-center justify-between gap-4 flex-wrap" data-testid={`promotion-row-${p.id}`}>
              <div>
                <p className="font-extrabold text-sm uppercase">{p.name} {!p.active && <span className="text-midnight-black/40">(inactive)</span>}</p>
                <p className="text-xs font-medium text-midnight-black/60">
                  {p.discount_type === "percentage" ? `${p.discount_value}% off` : `$${p.discount_value} off`} · {p.applies_to} · {p.scope} · {p.start_date} → {p.end_date}
                </p>
                {p.promo_code && <p className="text-[10px] font-bold uppercase tracking-wide text-stranger-orange mt-1">Code: {p.promo_code}</p>}
              </div>
              <div className="flex items-center gap-2">
                <button data-testid={`edit-promotion-${p.id}`} onClick={() => openEdit(p)} className="p-1.5 rounded-full border border-soft-gray hover:border-stranger-orange">
                  <Pencil className="w-3.5 h-3.5" />
                </button>
                <button data-testid={`delete-promotion-${p.id}`} onClick={() => setDeleteTarget(p.id)} className="p-1.5 rounded-full border border-soft-gray hover:border-red-500 hover:text-red-500">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!form} onOpenChange={(v) => !v && setForm(null)}>
        <DialogContent data-testid="promotion-form-modal" className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-extrabold uppercase">{editingId ? "Edit Promotion" : "New Promotion"}</DialogTitle>
          </DialogHeader>
          {form && (
            <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
              <Input data-testid="promo-name-input" placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              <Input data-testid="promo-description-input" placeholder="Description (optional)" value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              <div className="grid grid-cols-2 gap-3">
                <Input data-testid="promo-start-date-input" type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
                <Input data-testid="promo-end-date-input" type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Select value={form.discount_type} onValueChange={(v) => setForm({ ...form, discount_type: v })}>
                  <SelectTrigger data-testid="promo-discount-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">Percentage Off</SelectItem>
                    <SelectItem value="fixed">Fixed Amount Off</SelectItem>
                  </SelectContent>
                </Select>
                <Input data-testid="promo-discount-value-input" type="number" placeholder="Discount value" value={form.discount_value} onChange={(e) => setForm({ ...form, discount_value: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Select value={form.applies_to} onValueChange={(v) => setForm({ ...form, applies_to: v })}>
                  <SelectTrigger data-testid="promo-applies-to-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="strangers">Strangers</SelectItem>
                    <SelectItem value="photographers">Photographers</SelectItem>
                    <SelectItem value="both">Both</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={form.scope} onValueChange={(v) => setForm({ ...form, scope: v })}>
                  <SelectTrigger data-testid="promo-scope-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Sessions</SelectItem>
                    <SelectItem value="session">Specific Session</SelectItem>
                    <SelectItem value="photographer">Specific Photographer</SelectItem>
                    <SelectItem value="user">Specific User</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {form.scope !== "all" && (
                <Input data-testid="promo-scope-id-input" placeholder={`${form.scope} ID`} value={form.scope_id || ""} onChange={(e) => setForm({ ...form, scope_id: e.target.value })} />
              )}
              <Input data-testid="promo-code-input" placeholder="Promo code (optional, informational)" value={form.promo_code || ""} onChange={(e) => setForm({ ...form, promo_code: e.target.value })} />
              <div className="grid grid-cols-2 gap-3">
                <Input data-testid="promo-usage-limit-input" type="number" placeholder="Total usage limit" value={form.usage_limit} onChange={(e) => setForm({ ...form, usage_limit: e.target.value })} />
                <Input data-testid="promo-per-user-limit-input" type="number" placeholder="Per-user limit" value={form.per_user_limit} onChange={(e) => setForm({ ...form, per_user_limit: e.target.value })} />
              </div>
              <div className="flex items-center justify-between py-2">
                <p className="text-sm font-bold">Active</p>
                <Switch data-testid="promo-active-switch" checked={!!form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" data-testid="promo-cancel-button" onClick={() => setForm(null)} className="rounded-full font-bold">Cancel</Button>
            <Button data-testid="promo-save-button" disabled={saving || !form?.name || !form?.start_date || !form?.end_date} onClick={save} className="bg-stranger-orange hover:bg-orange-600 text-clean-white font-bold rounded-full">
              {saving ? "Saving..." : editingId ? "SAVE CHANGES" : "CREATE PROMOTION"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(v) => !v && setDeleteTarget(null)}
        title="Delete this promotion?"
        description="This cannot be undone. Bookings already using it are unaffected."
        confirmLabel="DELETE"
        destructive
        onConfirm={() => remove(deleteTarget)}
      />
    </div>
  );
};
